<script setup>
import { requestConclusionResult } from 'dashboard/composables/useConclusionResultPrompt';
import { computed, onUnmounted, ref, onMounted, watch } from 'vue';
import { getUnixTime } from 'date-fns';
import { useRoute, useRouter } from 'vue-router';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AssignmentTransferRequestsAPI from 'dashboard/api/aceleres/assignmentTransferRequests';
import TeamNumbersAPI from 'dashboard/api/aceleres/teamNumbers';
import AceleresOwnershipTransferModal from './AceleresOwnershipTransferModal.vue';
import CustomSnoozeModal from 'dashboard/components/CustomSnoozeModal.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';
import { activateMenu, deactivateMenu } from './contextMenu/activeMenuRegistry';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SNOOZE_NOTIFICATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import { useMediaQuery } from '@vueuse/core';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';
import { findSnoozeTime } from 'dashboard/helper/snoozeHelpers';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import ContactClassificationPicker from './ContactClassificationPicker.vue';
import FollowupModal from './FollowupModal.vue';
import {
  CONTACT_CLASSIFICATION_ATTRIBUTE,
  resolveContactClassification,
  resolveContactClassificationSelection,
  shouldReconcileTeamNumberClassification,
} from 'dashboard/constants/contactClassifications';

const props = defineProps({
  activeNotification: {
    type: Object,
    default: null,
  },
  // Aceleres 2026-08-19: a conexao da conversa, para o telemovel a mostrar
  // aqui dentro. Vem PRONTA do cabecalho (`instanceTitle` em
  // ConversationHeader.vue) em vez de ser recalculada: quem sabe resolver o
  // nome da conexao e ele — passa por espelho do Aceleres, por quatro nomes
  // possiveis nos atributos da caixa e por um limpador de prefixo/sufixo.
  // Refazer essa conta aqui seria uma segunda fonte de verdade para o mesmo
  // texto, e as duas divergiriam no primeiro ajuste.
  instanceLabel: {
    type: String,
    default: '',
  },
  instanceIcon: {
    type: String,
    default: '',
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

// O pedido e SO para telemovel: no computador a conexao continua no cartao do
// cabecalho, onde ha largura para ela.
const isMobileViewport = useMediaQuery('(max-width: 767px)');

const store = useStore();
const isUpdatingContactBlock = ref(false);
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const transferCopy = {
  title: 'Transferir conversa',
  subtitle: 'Escolha o novo responsável',
  searchPlaceholder: 'Buscar usuário',
  currentUserLabel: '(você)',
  // O próprio perfil usa um identificador de ação, não o status de presença.
  currentUserStatus: 'Você',
  availableGroup: 'Disponíveis',
  availableStatus: 'Disponível',
  offlineGroup: 'Offline',
  offlineStatus: 'Offline',
  emptyState: 'Nenhum usuário encontrado',
};

const ensureMovementReasonPreferenceScript = () => {
  if (typeof window.__aclGetMovementReasonPreference === 'function') {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const existingScript = document.querySelector(
      'script[data-aceleres-movement-reason-preference]'
    );
    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src =
      '/aceleres-funnel-movement-reason-preference.js?v=20260803-funnel-regression-v1';
    script.dataset.aceleresMovementReasonPreference = 'true';
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', resolve, { once: true });
    document.head.appendChild(script);
  });
};

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showTransferMenu, toggleTransferMenu] = useToggle(false);
const [showClassificationMenu, toggleClassificationMenu] = useToggle(false);
const classificationUpdating = ref(false);
const classificationMenuTitle = 'Classificação do contato';
const showCustomSnoozeModal = ref(false);
const showOwnershipModal = ref(false);
const ownershipStatus = ref(null);
const ownershipStatusLoading = ref(false);
const ownershipSubmitting = ref(false);
let ownershipStatusPollId = null;

// Transferência de ATENDIMENTO: fluxo irmão do de carteirização acima, mas
// decide quem conduz a conversa agora, não quem é dono do lead.
const showAttendanceModal = ref(false);
const attendanceStatus = ref(null);
const attendanceSubmitting = ref(false);

// Guarda anti-clickaway: o clique que abre o sub-menu "Transferir" faz o Vue
// desmontar o DropdownMenu; o v-on-clickaway avalia no tick seguinte, não
// encontra mais o botão clicado dentro do container e dispara closeAll(),
// fechando tudo no mesmo evento. closeAll ignora esse único ciclo. Ver closeAll.
let transferJustOpened = false;
let classificationJustOpened = false;

// Aceleres: o menu do cabecalho e o menu de contexto do cartao usam o mesmo
// registo, para que abrir um feche o outro — dois menus abertos ao mesmo tempo
// nao acontecem.
const headerMenuOwner = Symbol('conversation-header-more-actions');

const currentChat = computed(() => store.getters.getSelectedChat);
const currentUser = computed(() => store.getters.getCurrentUser);
const notificationMeta = computed(() => store.getters['notifications/getMeta']);
const activeInboxNotification = computed(() => {
  if (props.activeNotification?.id) return props.activeNotification;
  if (route.name !== 'inbox_view_conversation') return null;

  const notificationsGetter =
    store.getters['notifications/getFilteredNotifications'];
  const records =
    typeof notificationsGetter === 'function'
      ? notificationsGetter({ sortOrder: 'desc' })
      : [];
  const conversationId = Number(route.params.id);

  return (
    records.find(
      notification => Number(notification.primary_actor?.id) === conversationId
    ) || null
  );
});
const isNotificationContext = computed(() =>
  Boolean(activeInboxNotification.value?.id)
);
const { isAdmin } = useAdmin();
const isTrainingChat = computed(() =>
  isTrainingConversation(currentChat.value, currentUser.value)
);
const contactClassification = computed(() =>
  resolveContactClassification(props.contact)
);
const isCommercialContact = computed(
  () => contactClassification.value.value === 'commercial_lead'
);

// ─── Status helpers (resolve / reopen / pending / snoozed) ───
const isOpen = computed(
  () => currentChat.value?.status === wootConstants.STATUS_TYPE.OPEN
);
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](currentChat.value?.inbox_id)
);
// Ser uma conversa PESSOAL e uma identidade; OCUPAR a caixa Pessoais e outra
// pergunta -- e e esta que decide o menu. Enquanto isto aqui respondia a
// primeira, "Etapa" e IA ficavam escondidas PARA SEMPRE numa conversa de
// instancia pessoal, mesmo depois de ela ser assumida e virar lead com card no
// /funil. O backend ja respondia a segunda (Aceleres::PersonalBox#occupied?) e
// publica a resposta pronta em `aceleres_personal_box`; usar uma terceira copia
// da regra aqui foi exatamente o que divergiu.
const isPersonalConversationIdentity = computed(() => {
  const attrs = currentChat.value?.additional_attributes || {};
  const inboxAttrs = currentInbox.value?.additional_attributes || {};

  return (
    isAceleresIndividualOwn(inboxAttrs.connection_type) ||
    Number(attrs.personal_owner_chatwoot_user_id) > 0 ||
    Number(attrs.personal_origin_chatwoot_user_id) > 0 ||
    Number(attrs.owner_chatwoot_user_id) > 0
  );
});
const isPersonalConversation = computed(() => {
  if (!isPersonalConversationIdentity.value) return false;

  const box = currentChat.value?.aceleres_personal_box;
  if (box !== undefined && box !== null) return Boolean(box);

  // Fallback so para payload sem o campo (evento antigo, conversa vinda de um
  // cache anterior a esta versao): conservador do lado de "ainda e pessoal",
  // que e o lado reversivel -- o menu completo aparece no proximo refetch.
  const attrs = currentChat.value?.additional_attributes || {};
  return !attrs.personal_claimed_at;
});
// ─── Funil: label da etapa atual ───
const FUNNEL_LABEL_NAMES = {
  'nao-contactado': 'Não Contactado',
  ia: 'IA',
  'contato-inicial': 'Contato Inicial',
  desenvolvimento: 'Desenvolvimento',
  negociacao: 'Negociação',
  'link-pagamento-gerado': 'Link de Pagamento',
  'venda-feita': 'Venda Feita',
  'nao-qualificado': 'Não Qualificado',
  perdido: 'Perdido',
  'sem-atendimento': 'Sem Atendimento',
};
const FUNNEL_ORDER = [
  'nao-contactado',
  'ia',
  'contato-inicial',
  'desenvolvimento',
  'negociacao',
  'link-pagamento-gerado',
  'venda-feita',
  'nao-qualificado',
  'perdido',
  'sem-atendimento',
];
const conversationLabels = computed(() => {
  const id = currentChat.value?.id;
  const fromStore = id
    ? store.getters['conversationLabels/getConversationLabels'](id)
    : null;
  if (Array.isArray(fromStore) && fromStore.length) return fromStore;
  return currentChat.value?.labels || [];
});
const funnelStageLabel = computed(() => {
  const labels = conversationLabels.value || [];
  const present = FUNNEL_ORDER.filter(
    s => labels.includes(s) && FUNNEL_LABEL_NAMES[s]
  );
  if (!present.length) return 'Funil';
  // Aceleres: 'sem-atendimento' é uma flag (conversa sem responsável), não uma
  // etapa real do funil. O backend Laravel (FunilLabelSync) a preserva como label
  // não-funil ao mover de etapa — então ela coexiste com 'negociacao', etc. Como
  // 'sem-atendimento' é a última em FUNNEL_ORDER, ela sobrescrevia a etapa real no
  // botão (bug: "em negociação aparecia Sem Atendimento"). Agora só retorna
  // 'Sem Atendimento' se for o ÚNICO label de funil presente.
  const realStages = present.filter(s => s !== 'sem-atendimento');
  const chosen = realStages.length ? realStages : present;
  return FUNNEL_LABEL_NAMES[chosen[chosen.length - 1]];
});

// ─── Transferir: lista de agentes ───
// Fonte: inboxAssignableAgents (agents atribuíveis do inbox da conversa) via
// useAgentsList — o mesmo store usado pelos pickers que funcionam (contextMenu,
// AgentSelector). O store global agents/getAgents vinha vazio nesta view,
// deixando o sub-menu "Transferir" sem agentes.
const { assignableAgents } = useAgentsList(false);

// Inbox de instancia pessoal tem UM membro so: o dono do numero. Como
// `assignableAgents` sao os membros da inbox, o sub-menu "Transferir" ficava
// com o proprio usuario e mais ninguem -- nao havia para quem transferir.
// Nesse caso a fonte passa a ser os agentes da CONTA; quem entrar como
// responsavel ganha o acesso a inbox no backend (AssignmentService).
const accountAgents = useMapGetter('agents/getVerifiedAgents');
const isIndividualOwnInbox = computed(() =>
  isAceleresIndividualOwn(
    currentInbox.value?.additional_attributes?.connection_type
  )
);
const transferAgentSource = computed(() => {
  if (!isIndividualOwnInbox.value) return assignableAgents.value || [];
  const agents = accountAgents.value || [];
  return agents.length ? agents : assignableAgents.value || [];
});
const currentUserId = computed(() => Number(currentUser.value?.id || 0));
const currentAccountId = computed(() =>
  Number(store.getters.getCurrentAccountId || 0)
);
const transferAgents = computed(() => {
  const currentAssigneeId = Number(currentChat.value?.meta?.assignee?.id || 0);
  const currentUserRecord = currentUser.value;
  const currentAccount = (currentUserRecord?.accounts || []).find(
    account => Number(account.id) === currentAccountId.value
  );
  const currentUserAvailability =
    currentAccount?.availability_status ||
    currentAccount?.availability ||
    currentUserRecord?.availability_status ||
    'offline';
  const agents = (transferAgentSource.value || []).map(agent =>
    Number(agent.id) === currentUserId.value
      ? { ...agent, availability_status: currentUserAvailability }
      : agent
  );

  if (
    currentUserId.value > 0 &&
    !agents.some(agent => Number(agent.id) === currentUserId.value)
  ) {
    agents.push({
      ...currentUserRecord,
      id: currentUserId.value,
      confirmed: true,
      role: currentUserRecord.role || 'agent',
      availability_status: currentUserAvailability,
    });
  }

  return agents
    .filter(
      agent =>
        Number(agent.id) === currentUserId.value ||
        Number(agent.id) !== currentAssigneeId
    )
    .sort((left, right) => {
      if (Number(left.id) === currentUserId.value) return -1;
      if (Number(right.id) === currentUserId.value) return 1;
      return 0;
    });
});

// Busca + agrupamento por disponibilidade no sub-menu "Transferir"
const transferSearch = ref('');
const filteredTransferAgents = computed(() => {
  const q = transferSearch.value.trim().toLowerCase();
  const list = transferAgents.value || [];
  if (!q) return list;
  return list.filter(a => (a.name || '').toLowerCase().includes(q));
});
const currentTransferAgent = computed(() =>
  filteredTransferAgents.value.find(
    agent => Number(agent.id) === currentUserId.value
  )
);
const otherTransferAgents = computed(() =>
  filteredTransferAgents.value.filter(
    agent => Number(agent.id) !== currentUserId.value
  )
);
const onlineTransferAgents = computed(() =>
  otherTransferAgents.value.filter(a => a.availability_status === 'online')
);
const offlineTransferAgents = computed(() =>
  otherTransferAgents.value.filter(a => a.availability_status !== 'online')
);

// ─── Transferência de atendimento com aprovação ───
const pendingAttendanceRequest = computed(
  () => attendanceStatus.value?.pending_request || null
);
const canRequestAttendance = computed(
  () => attendanceStatus.value?.can_request === true
);
const attendanceAgents = computed(
  () => attendanceStatus.value?.eligible_targets || []
);
const currentAttendant = computed(
  () => attendanceStatus.value?.current_assignee || null
);
const attendanceActionLabel = computed(() => {
  if (!canRequestAttendance.value) return 'Transferir conversa';
  if (pendingAttendanceRequest.value) {
    return 'Transferência aguardando autorização';
  }
  return 'Solicitar transferência de atendimento';
});

const fetchAttendanceStatus = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) {
    attendanceStatus.value = null;
    return;
  }

  try {
    const { data } = await AssignmentTransferRequestsAPI.status(conversationId);
    if (Number(currentChat.value?.id) === Number(conversationId)) {
      attendanceStatus.value = data?.payload || null;
    }
  } catch {
    // Mantém o último estado conhecido: limpar aqui destravaria a ação
    // enquanto uma solicitação pendente ainda existe.
  }
};

const openAttendanceModal = async () => {
  toggleTransferMenu(false);
  toggleDropdown(false);
  await fetchAttendanceStatus();

  if (pendingAttendanceRequest.value) {
    useAlert(
      'Já existe uma transferência deste atendimento aguardando o gestor'
    );
    return;
  }
  if (!attendanceAgents.value.length) {
    useAlert('Nenhum vendedor disponível para receber este atendimento');
    return;
  }
  showAttendanceModal.value = true;
};

const submitAttendanceRequest = async ({ targetAssigneeId, justification }) => {
  attendanceSubmitting.value = true;
  try {
    const { data } = await AssignmentTransferRequestsAPI.create({
      conversationId: currentChat.value.id,
      targetAssigneeId,
      justification,
    });
    showAttendanceModal.value = false;
    await fetchAttendanceStatus();
    useAlert(
      data?.already_pending
        ? 'Já existe uma transferência deste atendimento aguardando o gestor'
        : 'Solicitação de transferência enviada ao gestor'
    );
  } catch (error) {
    // Cair aqui NÃO significa que a solicitação falhou. Sob carga, o
    // Rack::Timeout corta a resposta em 35s e devolve 500 DEPOIS de o pedido
    // já estar gravado, com nota no histórico e revisores notificados — o
    // usuário via o erro e o modal aberto para uma ação que tinha dado certo.
    // Antes de declarar falha, conferimos o estado real no servidor.
    let confirmed = false;
    try {
      await fetchAttendanceStatus();
      confirmed = !!pendingAttendanceRequest.value;
    } catch (_) {
      confirmed = false;
    }

    if (confirmed) {
      showAttendanceModal.value = false;
      useAlert('Solicitação de transferência enviada ao gestor');
      return;
    }

    useAlert(
      error?.response?.data?.error ||
        'Não foi possível solicitar a transferência do atendimento'
    );
  } finally {
    attendanceSubmitting.value = false;
  }
};

const transferTo = agent => {
  toggleTransferMenu(false);
  toggleDropdown(false);
  store
    .dispatch('assignAgent', {
      conversationId: currentChat.value.id,
      agentId: agent.id,
    })
    .then(() => useAlert(`Conversa transferida para ${agent.name}`))
    .catch(error => {
      // O backend barra a transferência direta de quem não pode revisar
      // (AssignmentsController#transfer_requires_approval?). Em vez de mostrar
      // "falhou", conduzimos o usuário ao caminho que ele de fato tem: abrir
      // uma solicitação para o gestor.
      if (error?.response?.data?.requires_transfer_request) {
        openAttendanceModal();
        return;
      }
      useAlert(
        error?.response?.data?.error || 'Falha ao transferir a conversa'
      );
    });
};

// ─── Alteração de carteirização ───
const pendingOwnershipRequest = computed(
  () => ownershipStatus.value?.pending_request || null
);
const currentPortfolioOwner = computed(
  () => ownershipStatus.value?.current_owner || null
);
const canRequestOwnership = computed(
  () => !isAdmin.value && ownershipStatus.value?.can_request === true
);
const canManageOwnership = computed(
  () => isAdmin.value && ownershipStatus.value?.can_manage === true
);
const canOpenOwnership = computed(
  () => canManageOwnership.value || canRequestOwnership.value
);
const ownershipActionLabel = computed(() => {
  if (canManageOwnership.value) return 'Alterar carteirização';
  if (pendingOwnershipRequest.value)
    return 'Carteirização aguardando autorização';
  return 'Solicitar alteração de carteirização';
});
const ownershipAgents = computed(() => {
  return ownershipStatus.value?.eligible_targets || [];
});
const ownershipStatusRequests = new Map();

const fetchOwnershipStatus = () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) {
    ownershipStatus.value = null;
    return Promise.resolve();
  }
  if (ownershipStatusRequests.has(conversationId)) {
    return ownershipStatusRequests.get(conversationId);
  }

  const request = (async () => {
    ownershipStatusLoading.value = true;
    try {
      const { data } =
        await ConversationTransferRequestsAPI.status(conversationId);
      if (Number(currentChat.value?.id) === Number(conversationId)) {
        ownershipStatus.value = data?.payload || null;
      }
    } catch {
      // Preserva o ultimo estado durante falhas transitorias. Limpar aqui
      // liberaria a acao enquanto ainda existe uma solicitacao pendente.
    } finally {
      ownershipStatusRequests.delete(conversationId);
      ownershipStatusLoading.value = ownershipStatusRequests.size > 0;
    }
  })();

  ownershipStatusRequests.set(conversationId, request);
  return request;
};

const stopOwnershipStatusPolling = () => {
  if (!ownershipStatusPollId) return;

  window.clearInterval(ownershipStatusPollId);
  ownershipStatusPollId = null;
};

const refreshPendingOwnershipStatus = () => {
  if (!pendingOwnershipRequest.value) return;
  if (document.visibilityState !== 'visible') return;

  fetchOwnershipStatus();
};

const startOwnershipStatusPolling = () => {
  if (ownershipStatusPollId || !pendingOwnershipRequest.value) return;

  ownershipStatusPollId = window.setInterval(
    refreshPendingOwnershipStatus,
    10000
  );
};

const onOwnershipRequestRealtimeUpdate = async payload => {
  const activeAccountId = Number(store.getters.getCurrentAccountId);
  if (Number(payload?.account_id) !== activeAccountId) return;
  if (Number(payload?.conversation_id) !== Number(currentChat.value?.id)) {
    return;
  }

  await fetchOwnershipStatus();

  const isOwnRequest =
    Number(payload?.requester_id) === Number(currentUser.value?.id);
  const decidedByAnotherUser =
    Number(payload?.decided_by_id) !== Number(currentUser.value?.id);
  if (!isOwnRequest || !decidedByAnotherUser) return;

  if (payload?.status === 'approved') {
    useAlert('Sua alteração de carteirização foi aprovada');
  } else if (payload?.status === 'rejected') {
    useAlert('Sua alteração de carteirização foi rejeitada');
  }
};

const openOwnershipModal = async () => {
  toggleDropdown(false);
  await fetchOwnershipStatus();

  if (!ownershipAgents.value.length) {
    useAlert('Nenhum outro vendedor disponível para receber a carteirização');
    return;
  }
  showOwnershipModal.value = true;
};

const submitOwnershipRequest = async ({ targetAssigneeId, justification }) => {
  ownershipSubmitting.value = true;
  try {
    const { data } = canManageOwnership.value
      ? await ConversationTransferRequestsAPI.managerUpdate({
          conversationId: currentChat.value.id,
          targetAssigneeId,
        })
      : await ConversationTransferRequestsAPI.create({
          conversationId: currentChat.value.id,
          targetAssigneeId,
          justification,
        });
    showOwnershipModal.value = false;
    await fetchOwnershipStatus();
    if (canManageOwnership.value) {
      useAlert('Carteirização alterada com sucesso');
    } else {
      useAlert(
        data?.already_pending
          ? 'Já existe uma alteração de carteirização aguardando autorização'
          : 'Solicitação de alteração de carteirização enviada ao gestor'
      );
    }
  } catch (error) {
    // Mesmo caso do submitAttendanceRequest: o 500 do Rack::Timeout chega
    // depois de a alteração já ter sido gravada. O que confirma sucesso muda
    // conforme o fluxo — gestor troca o dono na hora, vendedor deixa um pedido
    // pendente —, então cada um checa o seu próprio efeito.
    let confirmed = false;
    try {
      await fetchOwnershipStatus();
      confirmed = canManageOwnership.value
        ? Number(currentPortfolioOwner.value?.id) === Number(targetAssigneeId)
        : !!pendingOwnershipRequest.value;
    } catch (_) {
      confirmed = false;
    }

    if (confirmed) {
      showOwnershipModal.value = false;
      useAlert(
        canManageOwnership.value
          ? 'Carteirização alterada com sucesso'
          : 'Solicitação de alteração de carteirização enviada ao gestor'
      );
      return;
    }

    useAlert(
      error?.response?.data?.error ||
        'Não foi possível solicitar a alteração de carteirização'
    );
  } finally {
    ownershipSubmitting.value = false;
  }
};

// ─── Aurora IA: estado + toggle (usa endpoints do aceleres-aurora-btn.js) ───
const resolveAceleresApiBase = () => {
  const host = String(window.location.host || '').toLowerCase();
  // Aceleres -- app.zoraxy.ai fala com o Laravel pelo proxy same-origin do
  // proprio nginx (location /aceleres-api/). Chamada cross-site para
  // aceleres.ai perde o cookie de sessao, e o heuristico de 'zoraxy' abaixo
  // ainda mandaria a producao para o Laravel de homolog.
  if (host === 'app.zoraxy.ai') return '/aceleres-api';
  if (host === 'atendimento.zoraxy.ai') return '/aceleres-api';
  if (host === 'atendimento.aceleres.ai') return 'https://aceleres.ai';
  if (host.includes('zoraxy') || host.includes('homolog'))
    return 'https://homolog.aceleres.ai';
  return 'https://aceleres.ai';
};

const ACELERES_API = resolveAceleresApiBase();
let aclSessionPromise = null;
let auroraStatusRequestId = 0;
let auroraStatusAppliedRequestId = 0;
let auroraFocusRefreshTimer = null;
const auroraState = ref('off'); // 'off' | 'on' | 'unavailable' | 'loading' | 'pending'
const auroraReview = ref(null);
// A mesma resposta é consumida pelo card do Funil; não repetir aqui as
// condições que decidem se uma ação comum aparece ou não.
const sharedMenuActions = ref(null);
const showFollowupModal = ref(false);
const followupSubmitting = ref(false);
const followupSuccess = ref(false);
const showAuroraBlockedNotice = ref(false);
let auroraBlockedNoticeTimer = null;
const auroraBlockedNoticeTitle = 'IA bloqueada';
const auroraBlockedNoticeMessage =
  'Ative a IA em Configurações > Equipes IA e Rodízio (Automações).';
const auroraBlockedNoticeCloseLabel = 'Fechar aviso de IA bloqueada';

// Bypass de teste exclusivo do Lucas (homolog): ignora os gates normais da
// IA (privacidade, whitelist, humano respondeu, grupo). Ver
// AuroraActivateController::ativarBypassTeste / desativarBypassTeste.
const auroraBypassAvailable = ref(false);
const auroraBypassState = ref('off'); // 'off' | 'on' | 'loading'
const closeAuroraBlockedNotice = () => {
  showAuroraBlockedNotice.value = false;
  if (auroraBlockedNoticeTimer) {
    clearTimeout(auroraBlockedNoticeTimer);
    auroraBlockedNoticeTimer = null;
  }
};

const showAuroraBlockedNoticeForUser = () => {
  showAuroraBlockedNotice.value = true;
  toggleDropdown(false);
  if (auroraBlockedNoticeTimer) clearTimeout(auroraBlockedNoticeTimer);
  auroraBlockedNoticeTimer = setTimeout(() => {
    showAuroraBlockedNotice.value = false;
    auroraBlockedNoticeTimer = null;
  }, 7000);
};

const handleAuroraBlockedNoticeOutside = event => {
  if (event.target?.closest?.('.acl-aurora-blocked-notice')) return;
  closeAuroraBlockedNotice();
};

const readChatwootSessionInfo = () => {
  try {
    const match = document.cookie.match(/(?:^|;)\s*cw_d_session_info=([^;]*)/);
    if (!match) return null;
    const info = JSON.parse(decodeURIComponent(match[1]));
    if (!info?.['access-token'] || !info?.client || !info?.uid) return null;
    return info;
  } catch (_) {
    return null;
  }
};

const ensureAceleresSession = force => {
  if (!force && aclSessionPromise) return aclSessionPromise;

  aclSessionPromise = new Promise(resolve => {
    const info = readChatwootSessionInfo();
    if (!info) {
      resolve(false);
      return;
    }

    const body = new URLSearchParams();
    body.append('access_token', info['access-token'] || '');
    body.append('client', info.client || '');
    body.append('uid', info.uid || '');

    fetch(`${ACELERES_API}/api/kanban/zoraxy-login`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      body,
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => resolve(!!data?.success))
      .catch(() => resolve(false))
      .finally(() => {
        aclSessionPromise = null;
      });
  });

  return aclSessionPromise;
};

const fetchJsonWithSession = async (url, options = {}, retrying = false) => {
  const response = await fetch(url, options);
  const data = await response.json().catch(() => ({}));
  if (response.status !== 401 || retrying) return data;

  const ok = await ensureAceleresSession(true);
  if (!ok) return data;

  return fetchJsonWithSession(url, options, true);
};

const fetchAuroraStatus = async () => {
  const requestId = ++auroraStatusRequestId;
  const accId = store.getters.getCurrentAccountId;
  const convId = currentChat.value?.id;
  if (!accId || !convId) return null;
  try {
    const query = new URLSearchParams({
      chatwoot_account_id: String(accId),
      chatwoot_conversation_id: String(convId),
      status: currentChat.value?.status || '',
      personal: isPersonalConversation.value ? '1' : '0',
      commercial: isCommercialContact.value ? '1' : '0',
      ownership_available: canOpenOwnership.value ? '1' : '0',
      training: isTrainingChat.value ? '1' : '0',
    });
    const data = await fetchJsonWithSession(
      `${ACELERES_API}/api/aurora/status?${query.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      }
    );
    // Outra chamada pode ter falhado depois desta. Nesse caso, ainda precisamos
    // aplicar a resposta válida mais recente para a conversa aberta.
    if (
      String(accId) !== String(store.getters.getCurrentAccountId) ||
      String(convId) !== String(currentChat.value?.id) ||
      requestId < auroraStatusAppliedRequestId
    )
      return null;
    if (data?.success) auroraStatusAppliedRequestId = requestId;
    if (data?.disponivel === false) {
      auroraState.value = 'unavailable';
    } else if (data?.success && data?.pending) {
      auroraState.value = 'pending';
    } else if (data?.success && data?.ia_ativa_manual) {
      auroraState.value = 'on';
    } else {
      auroraState.value = 'off';
    }
    auroraReview.value = data?.pending_reviews?.[0] || null;
    sharedMenuActions.value = data?.success ? data.menu_actions || null : null;
    auroraBypassAvailable.value = !!data?.bypass_teste_disponivel;
    if (auroraBypassState.value !== 'loading') {
      auroraBypassState.value = data?.ia_bypass_teste ? 'on' : 'off';
    }
    return data;
  } catch (_) {
    /* mantém estado anterior em falha */
    return null;
  }
};

const refreshAuroraStatusWhenActive = () => {
  if (document.visibilityState === 'hidden') return;
  if (auroraFocusRefreshTimer) clearTimeout(auroraFocusRefreshTimer);
  auroraFocusRefreshTimer = setTimeout(() => {
    auroraFocusRefreshTimer = null;
    fetchAuroraStatus();
  }, 150);
};

const reviewAuroraOutput = async () => {
  const review = auroraReview.value;
  if (!review) return;

  const reasons = (review.reasons || []).join('\n- ');
  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(
    `A resposta automática foi bloqueada por risco potencial.\n\nMotivos:\n- ${reasons}\n\nPode haver falso positivo. Verifique o conteúdo com um superior antes de confirmar o envio.`
  );
  if (!confirmed) return;

  const data = await fetchJsonWithSession(`${ACELERES_API}/api/aurora/ativar`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      outbound_review_id: review.id,
      outbound_review_action: 'approve',
    }),
  });

  if (data?.success) {
    auroraReview.value = null;
    useAlert('Mensagem confirmada e enviada.');
    await fetchAuroraStatus();
  } else {
    useAlert(data?.message || 'Não foi possível confirmar a mensagem.');
  }
};

const toggleAurora = async () => {
  if (auroraState.value === 'unavailable' || auroraState.value === 'loading')
    return;
  const accId = store.getters.getCurrentAccountId;
  const convId = currentChat.value?.id;
  if (!accId || !convId) return;
  const wasOff = auroraState.value === 'off';
  auroraState.value = 'loading';
  const url = wasOff ? '/api/aurora/ativar' : '/api/aurora/desativar';
  try {
    const data = await fetchJsonWithSession(`${ACELERES_API}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        chatwoot_account_id: parseInt(accId, 10),
        chatwoot_conversation_id: parseInt(convId, 10),
      }),
    });
    if (data?.success) {
      auroraState.value = wasOff ? 'on' : 'off';
      let successMessage = 'IA desativada — atendimento volta ao humano';
      if (wasOff) {
        auroraState.value = data.pending ? 'pending' : 'on';
        if (data.dispatched) {
          successMessage = 'IA ativada e respondendo!';
        } else if (data.pending) {
          successMessage = 'IA aguardando 10 segundos de segurança';
        } else {
          successMessage = 'IA ativada para próxima mensagem';
        }
      }
      useAlert(successMessage);
    } else {
      auroraState.value = wasOff ? 'off' : 'on';
      useAlert(`Erro: ${data?.message || 'falha ao alternar IA'}`);
    }
  } catch (_) {
    auroraState.value = wasOff ? 'off' : 'on';
    useAlert('Erro de conexão — tente novamente');
  }
};

// ─── Bypass de teste (Lucas): ignora os gates normais, com confirmação
// explícita. Simples de propósito — sem estilo diferenciado no menu. ───
const toggleAuroraBypass = async () => {
  if (auroraBypassState.value === 'loading') return;
  const accId = store.getters.getCurrentAccountId;
  const convId = currentChat.value?.id;
  if (!accId || !convId) return;

  const wasOff = auroraBypassState.value === 'off';
  if (wasOff) {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Isto é um BYPASS DE TESTE exclusivo do Lucas: a IA vai responder nesta conversa ignorando privacidade, whitelist, checagem de humano respondeu e bloqueio de grupo.\n\nConfirma a ativação?'
    );
    if (!confirmed) return;
  }

  auroraBypassState.value = 'loading';
  const url = wasOff
    ? '/api/aurora/ativar-bypass-teste'
    : '/api/aurora/desativar-bypass-teste';
  try {
    const data = await fetchJsonWithSession(`${ACELERES_API}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        chatwoot_account_id: parseInt(accId, 10),
        chatwoot_conversation_id: parseInt(convId, 10),
      }),
    });
    if (data?.success) {
      auroraBypassState.value = wasOff ? 'on' : 'off';
      useAlert(
        wasOff
          ? 'Bypass de teste ativado (Lucas)'
          : 'Bypass de teste desativado'
      );
    } else {
      auroraBypassState.value = wasOff ? 'off' : 'on';
      useAlert(`Erro: ${data?.message || 'falha ao alternar bypass'}`);
    }
  } catch (_) {
    auroraBypassState.value = wasOff ? 'off' : 'on';
    useAlert('Erro de conexão — tente novamente');
  }
};

onMounted(fetchAuroraStatus);
onMounted(() => {
  window.addEventListener('focus', refreshAuroraStatusWhenActive);
  document.addEventListener('visibilitychange', refreshAuroraStatusWhenActive);
});
onMounted(() => {
  document.addEventListener('pointerdown', handleAuroraBlockedNoticeOutside);
});

// Refetch quando muda de conversa
const lastFetchedConvId = ref(null);
const ensureAuroraStatusFresh = () => {
  const id = currentChat.value?.id;
  if (id && id !== lastFetchedConvId.value) {
    lastFetchedConvId.value = id;
    fetchAuroraStatus();
  }
};

// ─── Resolve / Mover etapa ───
const resolveConversation = async () => {
  toggleDropdown(false);
  const conversationId = currentChat.value?.id;
  // Aceleres: o vendedor informa o tipo de conclusão; cancelar não conclui.
  const conclusionResult = await requestConclusionResult();
  if (!conclusionResult || !conversationId) return;
  store
    .dispatch('toggleStatus', {
      conversationId,
      status: wootConstants.STATUS_TYPE.RESOLVED,
      conclusionResult,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_STATUS')))
    .catch(() => useAlert('Falha ao concluir a conversa'));
};
const reopenConversation = () => {
  toggleDropdown(false);
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: wootConstants.STATUS_TYPE.OPEN,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_STATUS')))
    .catch(() => useAlert('Falha ao reabrir a conversa'));
};
const openFunnelModal = () => {
  toggleDropdown(false);
  ensureMovementReasonPreferenceScript().then(() => {
    // eslint-disable-next-line no-underscore-dangle
    const openMoveModal = window.__aclOpenMoveModal;
    if (typeof openMoveModal === 'function') {
      openMoveModal();
    } else {
      useAlert('Seletor de etapas indisponível — recarregue a página.');
    }
  });
};

const openSnoozeNotificationMenu = () => {
  if (!isNotificationContext.value) return;
  toggleDropdown(false);
  document.querySelector('ninja-keys')?.open({ parent: 'snooze_notification' });
};

const snoozeNotification = async snoozedUntil => {
  if (!activeInboxNotification.value?.id) return;

  try {
    await store.dispatch('notifications/snooze', {
      id: activeInboxNotification.value.id,
      snoozedUntil,
    });
    useAlert(t('INBOX.ALERTS.SNOOZE'));
  } catch {
    // Keep the current conversation open when the notification update fails.
  }
};

const onCmdSnoozeNotification = snoozeType => {
  if (!isNotificationContext.value) return;

  if (snoozeType === wootConstants.SNOOZE_OPTIONS.UNTIL_CUSTOM_TIME) {
    showCustomSnoozeModal.value = true;
  } else if (typeof snoozeType === 'number') {
    snoozeNotification(snoozeType);
  } else {
    snoozeNotification(findSnoozeTime(snoozeType) || null);
  }
};

onMounted(() => {
  emitter.on(CMD_SNOOZE_NOTIFICATION, onCmdSnoozeNotification);
  // O store global de agentes chega vazio nesta view quando nada mais o pediu
  // — era essa a razao do comentario em `useAgentsList` sobre ele. Para a
  // inbox pessoal ele e a UNICA fonte de destinatarios, entao pede-se aqui.
  if (!(accountAgents.value || []).length) {
    store.dispatch('agents/get');
  }
});

const scheduleCustomSnooze = customSnoozeTime => {
  showCustomSnoozeModal.value = false;
  if (customSnoozeTime) {
    snoozeNotification(getUnixTime(customSnoozeTime) || null);
  }
};

const deleteNotification = () => {
  if (!activeInboxNotification.value?.id) return;

  useTrack(INBOX_EVENTS.DELETE_NOTIFICATION);
  toggleDropdown(false);
  store
    .dispatch('notifications/delete', {
      notification: activeInboxNotification.value,
      unread_count: notificationMeta.value.unreadCount,
      count: notificationMeta.value.count,
    })
    .then(() => {
      useAlert(t('INBOX.ALERTS.DELETE'));
      router.replace({ name: 'inbox_view' });
    });
};

const onFunnelMoved = event => {
  const movedConversationId = Number(event?.detail?.conversationId);
  const selectedConversationId = Number(currentChat.value?.id);
  if (!movedConversationId || movedConversationId !== selectedConversationId) {
    return;
  }
  store.dispatch('conversationLabels/get', movedConversationId);
  fetchAuroraStatus();
};

// ─── Menu items ───
const actionMenuItems = computed(() => {
  if (isTrainingChat.value) return [];

  const items = [];
  const visible = sharedMenuActions.value || {};

  // Aceleres 2026-08-19: a conexao abre o menu no telemovel, onde ela deixou
  // de caber no cabecalho. Entra como item `disabled` de proposito: e uma
  // informacao, nao uma acao, e `disabled` e o que o DropdownMenu ja usa para
  // desenhar uma linha que nao se clica.
  if (isMobileViewport.value && props.instanceLabel) {
    items.push({
      icon: props.instanceIcon || 'i-lucide-plug',
      label: props.instanceLabel,
      action: 'instance-info',
      value: 'instance-info',
      disabled: true,
    });
  }

  // Concluir / Reabrir
  if (visible.resolve) {
    items.push({
      icon: 'i-lucide-check-check',
      label: 'Concluir',
      action: 'resolve',
      value: 'resolve',
    });
  }

  // Mover de etapa
  if (visible.stage) {
    items.push({
      icon: 'i-lucide-filter',
      label: `Etapa: ${funnelStageLabel.value}`,
      action: 'move_stage',
      value: 'move_stage',
    });
  }

  // Transferir. Quem pode revisar escolhe o destinatário direto no sub-menu;
  // o vendedor abre uma solicitação para o gestor, porque o backend barra a
  // troca direta de responsável (regra: a transferência de atendimento
  // solicitada pelo vendedor passa por aprovação).
  if (visible.transfer) {
    items.push({
      icon: pendingAttendanceRequest.value
        ? 'i-lucide-clock-3'
        : 'i-lucide-user-plus',
      label: attendanceActionLabel.value,
      action: canRequestAttendance.value ? 'attendance_transfer' : 'transfer',
      value: 'transfer',
      disabled: canRequestAttendance.value && !!pendingAttendanceRequest.value,
    });
  }

  if (visible.classification) {
    items.push({
      icon: contactClassification.value.icon,
      label: `Classificação: ${contactClassification.value.label}`,
      action: 'classification',
      value: 'classification',
    });
  }

  if (visible.ownership) {
    items.push({
      icon: pendingOwnershipRequest.value
        ? 'i-lucide-clock-3'
        : 'i-lucide-user-round-cog',
      label: ownershipActionLabel.value,
      action: 'ownership_transfer',
      value: 'ownership_transfer',
      disabled:
        ownershipStatusLoading.value ||
        (!canManageOwnership.value && !!pendingOwnershipRequest.value),
    });
  }

  // Ativar / Desativar IA
  if (visible.aurora) {
    let label = 'Ativar IA';
    if (auroraState.value === 'on') {
      label = 'Desativar IA';
    } else if (auroraState.value === 'pending') {
      label = 'Cancelar ativação (10s)';
    } else if (auroraState.value === 'loading') {
      label = 'Aguarde…';
    }
    items.push({
      icon: visible.aurora_disabled ? 'i-lucide-lock-keyhole' : 'i-lucide-bot',
      label,
      action: 'aurora_toggle',
      value: 'aurora_toggle',
      disabled: false,
      ariaDisabled: visible.aurora_disabled || auroraState.value === 'loading',
    });
  }

  // Bypass de teste (Lucas) — sempre logo abaixo do Ativar/Desativar IA
  // normal, sem estilo diferenciado (item de menu padrão, como os outros).
  if (auroraBypassAvailable.value) {
    let bypassLabel = 'Ativar IA (bypass Lucas)';
    if (auroraBypassState.value === 'on') {
      bypassLabel = 'Desativar bypass (Lucas)';
    } else if (auroraBypassState.value === 'loading') {
      bypassLabel = 'Aguarde…';
    }
    items.push({
      icon: 'i-lucide-flask-conical',
      label: bypassLabel,
      action: 'aurora_bypass_toggle',
      value: 'aurora_bypass_toggle',
      disabled: auroraBypassState.value === 'loading',
    });
  }

  if (auroraReview.value) {
    items.push({
      icon: 'i-lucide-shield-alert',
      label: 'Revisar resposta bloqueada',
      action: 'aurora_review',
      value: 'aurora_review',
    });
  }

  // Mute / Unmute
  if (visible.block && !currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
      disabled: isUpdatingContactBlock.value,
    });
  } else if (visible.block) {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
      disabled: isUpdatingContactBlock.value,
    });
  }

  if (isNotificationContext.value) {
    items.push({
      icon: 'i-lucide-bell-minus',
      label: t('INBOX.ACTION_HEADER.SNOOZE'),
      action: 'snooze_notification',
      value: 'snooze_notification',
    });
    items.push({
      icon: 'i-lucide-trash-2',
      label: t('INBOX.ACTION_HEADER.DELETE'),
      action: 'delete',
      value: 'delete_notification',
    });
  }

  // Mover para meus. So dentro da caixa Pessoais: `isPersonalConversation` ja
  // significa "ocupa a caixa Pessoais agora" (identidade pessoal + resposta do
  // backend em `aceleres_personal_box`), entao em Meus, Novos ou Outros o item
  // nao aparece. Quem executa e a ChatList, por BUS.
  if (
    !isNotificationContext.value &&
    isOpen.value &&
    isPersonalConversation.value
  ) {
    items.push({
      icon: 'i-lucide-user-plus',
      label: 'Mover para meus',
      action: 'move_to_mine',
      value: 'move_to_mine',
    });
  }

  // A transcricao contem todo o historico da conversa e fica restrita aos
  // perfis de Gestor, provisionados no Chatwoot como administrator.
  if (visible.transcript) {
    items.push({
      icon: 'i-lucide-share',
      label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
      action: 'send_transcript',
      value: 'send_transcript',
    });
  }

  items.push({
    icon: 'i-lucide-calendar-check',
    label: 'Criar Follow-up',
    action: 'followup',
    value: 'followup',
  });

  return items;
});

const handleAuroraToggleClick = async () => {
  const wasLoading = auroraState.value === 'loading';
  // O status local pode ter ficado preso após uma requisição anterior. A decisão
  // de ativar deve usar a leitura atual, que o Laravel também valida no POST.
  const status = await fetchAuroraStatus();
  if (!status?.success) {
    useAlert('Não foi possível verificar a IA. Tente novamente.');
    return;
  }
  if (status.disponivel === false || status.menu_actions?.aurora_disabled) {
    showAuroraBlockedNoticeForUser();
    return;
  }
  if (wasLoading) {
    useAlert('Estado da IA atualizado. Clique novamente para alterar.');
    return;
  }
  toggleDropdown(false);
  await toggleAurora();
};

const updateContactBlock = async blocked => {
  const conversationId = currentChat.value?.id;
  if (!conversationId || isTrainingChat.value || isUpdatingContactBlock.value) {
    return;
  }

  isUpdatingContactBlock.value = true;
  try {
    await store.dispatch(
      blocked ? 'muteConversation' : 'unmuteConversation',
      conversationId
    );
    useAlert(
      t(
        blocked
          ? 'CONTACT_PANEL.MUTED_SUCCESS'
          : 'CONTACT_PANEL.UNMUTED_SUCCESS'
      )
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível atualizar o bloqueio deste contato.'
    );
  } finally {
    isUpdatingContactBlock.value = false;
  }
};

const handleActionClick = async ({ action }) => {
  if (isTrainingChat.value) {
    toggleDropdown(false);
    toggleTransferMenu(false);
    return;
  }

  if (action === 'followup') {
    toggleDropdown(false);
    followupSuccess.value = false;
    showFollowupModal.value = true;
  } else if (action === 'resolve') {
    resolveConversation();
  } else if (action === 'reopen') {
    reopenConversation();
  } else if (action === 'move_stage') {
    openFunnelModal();
  } else if (action === 'transfer') {
    // Marca a abertura para que o clickaway (closeAll) ignore este clique.
    transferJustOpened = true;
    transferSearch.value = '';
    const inboxId = currentChat.value?.inbox_id;
    if (inboxId) {
      store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
    }
    toggleTransferMenu();
    setTimeout(() => {
      transferJustOpened = false;
    }, 0);
  } else if (action === 'attendance_transfer') {
    openAttendanceModal();
  } else if (action === 'ownership_transfer') {
    openOwnershipModal();
  } else if (action === 'classification') {
    classificationJustOpened = true;
    toggleClassificationMenu();
    setTimeout(() => {
      classificationJustOpened = false;
    }, 0);
  } else if (action === 'snooze_notification') {
    openSnoozeNotificationMenu();
  } else if (action === 'delete' && isNotificationContext.value) {
    deleteNotification();
  } else if (action === 'aurora_toggle') {
    handleAuroraToggleClick();
  } else if (action === 'aurora_review') {
    reviewAuroraOutput();
    toggleDropdown(false);
  } else if (action === 'aurora_bypass_toggle') {
    toggleAuroraBypass();
    toggleDropdown(false);
  } else if (action === 'mute') {
    toggleDropdown(false);
    await updateContactBlock(true);
  } else if (action === 'unmute') {
    toggleDropdown(false);
    await updateContactBlock(false);
  } else if (action === 'send_transcript') {
    toggleDropdown(false);
    toggleEmailModal();
  } else if (action === 'move_to_mine') {
    toggleDropdown(false);
    emitter.emit(BUS_EVENTS.ACELERES_MOVE_TO_MINE_REQUESTED, {
      conversationId: currentChat.value?.id,
    });
  }
};

const createFollowup = async payload => {
  const accountId = store.getters.getCurrentAccountId;
  const conversationId = currentChat.value?.id;
  if (!accountId || !conversationId) return;
  followupSubmitting.value = true;
  try {
    const data = await fetchJsonWithSession(`${ACELERES_API}/api/followups`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        client_name: props.contact?.name || null,
        client_phone: props.contact?.phone_number || props.contact?.phone || null,
        chatwoot_account_id: accountId,
        chatwoot_conversation_id: conversationId,
      }),
    });
    if (!data?.success) throw new Error(data?.message || 'Não foi possível criar o Follow-up.');
    followupSuccess.value = true;
  } catch (error) {
    useAlert(error.message || 'Não foi possível criar o Follow-up.');
  } finally {
    followupSubmitting.value = false;
  }
};

const closeAll = () => {
  // Ignora o clique que acabou de abrir o sub-menu "Transferir": o clickaway
  // dispara no mesmo evento (após o Vue desmontar o DropdownMenu) e fecharia
  // tudo. O guard transferJustOpened (limpo em setTimeout 0 / aqui) sinaliza
  // essa abertura.
  if (transferJustOpened || classificationJustOpened) {
    transferJustOpened = false;
    classificationJustOpened = false;
    return;
  }
  toggleDropdown(false);
  toggleTransferMenu(false);
  toggleClassificationMenu(false);
  deactivateMenu(headerMenuOwner);
};

// O que o registo chama para fechar este menu: sem o guard anti-clickaway do
// closeAll, que existe so para o clique que abre o sub-menu.
const forceCloseHeaderMenus = () => {
  transferJustOpened = false;
  classificationJustOpened = false;
  toggleDropdown(false);
  toggleTransferMenu(false);
  toggleClassificationMenu(false);
};

// Abrir sempre passa pelo registo: activateMenu fecha o menu que estiver aberto
// noutro sitio (o do cartao) antes de marcar este como ativo.
const onMoreActionsTriggerClick = () => {
  if (
    showActionsDropdown.value ||
    showTransferMenu.value ||
    showClassificationMenu.value
  ) {
    closeAll();
    return;
  }
  activateMenu({ owner: headerMenuOwner, close: forceCloseHeaderMenus });
  fetchAuroraStatus();
  toggleDropdown(true);
};

const classifyContact = async classification => {
  const contactId = Number(props.contact?.id);
  if (!contactId || classificationUpdating.value) return;

  const nextClassification = resolveContactClassificationSelection(
    props.contact,
    classification
  );
  const reconcileTeamNumber = shouldReconcileTeamNumberClassification(
    props.contact,
    classification
  );
  if (
    nextClassification === contactClassification.value.value &&
    !reconcileTeamNumber
  ) {
    toggleClassificationMenu(false);
    toggleDropdown(false);
    return;
  }

  classificationUpdating.value = true;
  try {
    if (reconcileTeamNumber) {
      await TeamNumbersAPI.mark(contactId);
      await store.dispatch('contacts/show', { id: contactId });
    } else {
      await store.dispatch('contacts/update', {
        id: contactId,
        customAttributes: {
          [CONTACT_CLASSIFICATION_ATTRIBUTE]: nextClassification,
        },
      });
    }
    toggleClassificationMenu(false);
    toggleDropdown(false);
    useAlert(`Contato classificado como ${contactClassification.value.label}`);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        'Não foi possível alterar a classificação do contato'
    );
  } finally {
    classificationUpdating.value = false;
  }
};

// Comandos globais (Cmd-bar / Ninja keys)
const mute = () => updateContactBlock(true);
const unmute = () => updateContactBlock(false);
emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);
emitter.on(
  BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
  onOwnershipRequestRealtimeUpdate
);
emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshPendingOwnershipStatus);
window.addEventListener('focus', refreshPendingOwnershipStatus);
document.addEventListener('visibilitychange', refreshPendingOwnershipStatus);
window.addEventListener('acl:funnel-moved', onFunnelMoved);

onUnmounted(() => {
  window.removeEventListener('focus', refreshAuroraStatusWhenActive);
  document.removeEventListener('visibilitychange', refreshAuroraStatusWhenActive);
  if (auroraFocusRefreshTimer) {
    clearTimeout(auroraFocusRefreshTimer);
    auroraFocusRefreshTimer = null;
  }
  deactivateMenu(headerMenuOwner);
  stopOwnershipStatusPolling();
  closeAuroraBlockedNotice();
  document.removeEventListener('pointerdown', handleAuroraBlockedNoticeOutside);
  emitter.off(CMD_SNOOZE_NOTIFICATION, onCmdSnoozeNotification);
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
  emitter.off(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    onOwnershipRequestRealtimeUpdate
  );
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshPendingOwnershipStatus);
  window.removeEventListener('focus', refreshPendingOwnershipStatus);
  document.removeEventListener(
    'visibilitychange',
    refreshPendingOwnershipStatus
  );
  window.removeEventListener('acl:funnel-moved', onFunnelMoved);
});

watch(pendingOwnershipRequest, pendingRequest => {
  if (pendingRequest) {
    startOwnershipStatusPolling();
  } else {
    stopOwnershipStatusPolling();
  }
});

watch(
  () => currentChat.value?.id,
  () => {
    sharedMenuActions.value = null;
    closeAuroraBlockedNotice();
    showOwnershipModal.value = false;
    showAttendanceModal.value = false;
    fetchOwnershipStatus();
    fetchAttendanceStatus();
    // Estava sendo chamado de dentro de `actionMenuItems` (computed). Como
    // ensureAuroraStatusFresh escreve em `lastFetchedConvId`, o computed
    // invalidava a si mesmo e a aba travava ao abrir conversa.
    // Efeito colateral pertence a um watch, nao a um computed.
    ensureAuroraStatusFresh();
  },
  { immediate: true }
);

// Status, ocupação da caixa Pessoais, classificação e permissão de carteira
// alteram a lista canônica. Reconsulta o mesmo contrato que o Funil usa.
watch(
  [
    () => currentChat.value?.status,
    isPersonalConversation,
    isCommercialContact,
    canOpenOwnership,
    isTrainingChat,
  ],
  () => fetchAuroraStatus()
);

// O assignee pode mudar por aprovação do gestor, rodízio ou automação. Quando
// isso acontece, quem pode solicitar deixa de ser o mesmo — o status precisa
// ser relido para o menu não oferecer uma ação que o backend vai negar.
watch(
  () => currentChat.value?.meta?.assignee?.id,
  () => fetchAttendanceStatus()
);

// Aceleres: o menu "Mais opcoes" e absoluto e ancorado no wrapper do botao,
// entao o CSS sozinho nao sabe a largura de .acl-conversation-header__actions.
// Medimos o container do cabecalho e repassamos ao menu por variavel CSS.
const actionsRoot = ref(null);
const headerActionsWidth = ref(null);
let headerActionsObserver = null;

const menuWidthStyle = computed(() =>
  !isMobileViewport.value && headerActionsWidth.value
    ? { '--acl-more-menu-width': `${headerActionsWidth.value}px` }
    : null
);

const observeHeaderActions = () => {
  const container = actionsRoot.value?.closest(
    '.acl-conversation-header__actions'
  );
  if (!container) return;
  const measure = () => {
    headerActionsWidth.value = Math.round(
      container.getBoundingClientRect().width
    );
  };
  measure();
  if (typeof ResizeObserver !== 'undefined') {
    headerActionsObserver = new ResizeObserver(measure);
    headerActionsObserver.observe(container);
  }
};

onMounted(observeHeaderActions);
onUnmounted(() => {
  headerActionsObserver?.disconnect();
  headerActionsObserver = null;
});
</script>

<template>
  <div
    ref="actionsRoot"
    class="relative flex items-center gap-2 actions--container"
  >
    <div
      v-if="!isTrainingChat"
      v-on-clickaway="closeAll"
      class="relative flex items-center group"
    >
      <span class="acl-tip-wrapper" data-acl-tip="Mais opções">
        <ButtonV4
          icon="i-lucide-more-vertical"
          size="md"
          variant="ghost"
          color="slate"
          class="acl-conversation-more-actions-trigger !size-10 !min-w-10 !p-0 !text-[#111827] md:group-hover:bg-n-alpha-2 [&>span]:!size-[20px] [&>span]:!flex-none [&>span]:!bg-current [&>span]:!text-[20px] [&>span]:!opacity-100 max-md:!size-12 max-md:!min-w-12 max-md:!rounded-full max-md:!border-0 max-md:!bg-transparent max-md:!shadow-none max-md:!outline-none max-md:hover:!bg-transparent max-md:focus:!bg-transparent max-md:active:!bg-transparent max-md:[&>span]:!size-[22px] max-md:[&>span]:!text-[22px]"
          :aria-label="t('CONVERSATION.HEADER.MORE_ACTIONS')"
          @click.stop="onMoreActionsTriggerClick()"
        />
      </span>
      <DropdownMenu
        v-if="
          showActionsDropdown && !showTransferMenu && !showClassificationMenu
        "
        :menu-items="actionMenuItems"
        button-class="acl-more-actions-item"
        label-class="acl-more-actions-item__label"
        class="acl-conversation-more-actions-menu mt-1 ltr:right-0 rtl:left-0 top-full max-md:[--acl-more-menu-width:min(320px,calc(100vw-24px))]"
        :style="menuWidthStyle"
        @action="handleActionClick"
      />
      <div
        v-if="showAuroraBlockedNotice"
        role="alert"
        aria-live="polite"
        class="acl-aurora-blocked-notice absolute top-full z-[70] mt-2 flex w-[min(340px,calc(100vw-24px))] gap-2 rounded-xl bg-n-alpha-3 p-3 text-left shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px] ltr:right-0 rtl:left-0"
        @click.stop
      >
        <span
          class="i-lucide-info mt-0.5 size-4 flex-shrink-0 text-n-blue-11"
          aria-hidden="true"
        />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ auroraBlockedNoticeTitle }}
          </p>
          <p class="mt-1 text-xs leading-5 text-n-slate-11">
            {{ auroraBlockedNoticeMessage }}
          </p>
        </div>
        <button
          type="button"
          class="flex size-6 flex-shrink-0 items-center justify-center rounded-md border-0 text-n-slate-11 hover:bg-n-alpha-1 max-md:!size-11"
          :aria-label="auroraBlockedNoticeCloseLabel"
          @click.stop="closeAuroraBlockedNotice"
        >
          <span class="i-lucide-x size-4" aria-hidden="true" />
        </button>
      </div>
      <FollowupModal
        :open="showFollowupModal"
        :contact="props.contact"
        :submitting="followupSubmitting"
        :success="followupSuccess"
        @close="showFollowupModal = false; followupSuccess = false"
        @submit="createFollowup"
      />
      <div
        v-if="showClassificationMenu"
        class="absolute top-full z-50 mt-1 rounded-xl bg-n-alpha-3 shadow-lg outline outline-1 outline-n-container backdrop-blur-[100px] ltr:right-0 rtl:left-0"
      >
        <div class="flex items-center justify-between gap-2 px-3 pb-1 pt-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ classificationMenuTitle }}
          </span>
          <button
            type="button"
            class="flex size-7 items-center justify-center rounded-lg border-0 text-n-slate-11 hover:bg-n-alpha-1 max-md:!size-11"
            :aria-label="$t('CLOSE')"
            @click="toggleClassificationMenu(false)"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
        <ContactClassificationPicker
          :model-value="contactClassification.value"
          :disabled="classificationUpdating"
          @select="classifyContact"
        />
      </div>
      <!-- Sub-menu de transferência -->
      <Transition name="acl-transfer">
        <div
          v-if="showTransferMenu"
          class="acl-transfer-popup absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0 w-[400px] max-h-[26rem] flex flex-col rounded-xl bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container shadow-lg overflow-hidden"
        >
          <!-- Cabeçalho -->
          <div class="flex items-center gap-2 px-2.5 py-2">
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ transferCopy.title }}
              </span>
              <span class="text-[11px] leading-tight text-n-slate-11 truncate">
                {{ transferCopy.subtitle }}
              </span>
            </div>
          </div>

          <!-- Busca -->
          <div class="px-2.5 pb-2">
            <div class="relative">
              <span
                class="absolute i-lucide-search size-3.5 top-2 left-2.5 text-n-slate-10"
              />
              <input
                v-model="transferSearch"
                type="search"
                :placeholder="transferCopy.searchPlaceholder"
                class="reset-base w-full h-8 py-2 pl-9 pr-2 text-sm border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
              />
            </div>
          </div>

          <!-- Lista -->
          <div class="flex-1 overflow-y-auto px-2 pb-2">
            <template v-if="currentTransferAgent">
              <button
                :key="'me-' + currentTransferAgent.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-all duration-200 ease-in-out hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2 border-b border-n-alpha-1 mb-1"
                @click="transferTo(currentTransferAgent)"
              >
                <Avatar
                  :name="currentTransferAgent.name"
                  :src="currentTransferAgent.thumbnail"
                  :status="currentTransferAgent.availability_status"
                  :size="26"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm text-n-slate-12 truncate">
                    {{ currentTransferAgent.name }}
                    <span class="text-n-slate-10">
                      {{ transferCopy.currentUserLabel }}
                    </span>
                  </span>
                  <span
                    class="text-[11px] leading-tight text-n-teal-10 truncate"
                  >
                    {{ transferCopy.currentUserStatus }}
                  </span>
                </div>
              </button>
            </template>

            <template v-if="onlineTransferAgents.length">
              <p
                class="px-2 pt-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
              >
                {{ transferCopy.availableGroup }}
              </p>
              <button
                v-for="agent in onlineTransferAgents"
                :key="'on-' + agent.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-all duration-200 ease-in-out hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
                @click="transferTo(agent)"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.thumbnail"
                  :status="agent.availability_status"
                  :size="26"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm text-n-slate-12 truncate">{{
                    agent.name
                  }}</span>
                  <span
                    class="text-[11px] leading-tight text-n-teal-10 truncate"
                  >
                    {{ transferCopy.availableStatus }}
                  </span>
                </div>
              </button>
            </template>

            <template v-if="offlineTransferAgents.length">
              <p
                class="px-2 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
              >
                {{ transferCopy.offlineGroup }}
              </p>
              <button
                v-for="agent in offlineTransferAgents"
                :key="'off-' + agent.id"
                type="button"
                class="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-all duration-200 ease-in-out hover:bg-n-alpha-1 dark:hover:bg-n-alpha-2"
                @click="transferTo(agent)"
              >
                <Avatar
                  :name="agent.name"
                  :src="agent.thumbnail"
                  :status="agent.availability_status"
                  :size="26"
                  rounded-full
                />
                <div class="flex flex-col min-w-0">
                  <span class="text-sm text-n-slate-12 truncate">{{
                    agent.name
                  }}</span>
                  <span
                    class="text-[11px] leading-tight text-n-slate-10 truncate"
                  >
                    {{ transferCopy.offlineStatus }}
                  </span>
                </div>
              </button>
            </template>

            <p
              v-if="
                !currentTransferAgent &&
                !onlineTransferAgents.length &&
                !offlineTransferAgents.length
              "
              class="px-2 py-4 text-center text-xs text-n-slate-11"
            >
              {{ transferCopy.emptyState }}
            </p>
          </div>
        </div>
      </Transition>
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <AceleresOwnershipTransferModal
      :show="showOwnershipModal"
      :current-owner="currentPortfolioOwner"
      :agents="ownershipAgents"
      :manager-mode="canManageOwnership"
      :submitting="ownershipSubmitting"
      @close="showOwnershipModal = false"
      @submit="submitOwnershipRequest"
    />
    <AceleresOwnershipTransferModal
      :show="showAttendanceModal"
      kind="atendimento"
      :current-owner="currentAttendant"
      :agents="attendanceAgents"
      :manager-mode="false"
      :submitting="attendanceSubmitting"
      @close="showAttendanceModal = false"
      @submit="submitAttendanceRequest"
    />
    <woot-modal
      v-model:show="showCustomSnoozeModal"
      :on-close="() => (showCustomSnoozeModal = false)"
    >
      <CustomSnoozeModal
        @close="showCustomSnoozeModal = false"
        @choose-time="scheduleCustomSnooze"
      />
    </woot-modal>
  </div>
</template>

<style scoped>
/* Tooltip estilo sidebar (#aceleres-topbar .acl-tb-btn::before) */
.acl-tip-wrapper {
  position: relative;
  display: inline-flex;
}
.acl-tip-wrapper::after {
  content: attr(data-acl-tip);
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #0f172a;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s;
  z-index: 1000;
}
.acl-tip-wrapper:hover::after {
  opacity: 1;
}

.acl-conversation-more-actions-menu {
  z-index: 1000;
  /* Largura espelha .acl-conversation-header__actions (medida no script);
     290px continua sendo o piso quando a medida ainda nao existe. */
  width: var(--acl-more-menu-width, 290px) !important;
  min-width: var(--acl-more-menu-width, 290px) !important;
  max-width: none !important;
}

/* Sub-menu Transferir — animação de entrada/saída (origem canto superior) */
.acl-transfer-enter-active,
.acl-transfer-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}
.acl-transfer-enter-from,
.acl-transfer-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(-2px);
}
</style>

<!-- Aceleres: os itens do menu vivem dentro do DropdownMenu compartilhado, que
     nao carrega o data-v deste componente. Por isso o estilo dos itens fica
     num bloco nao-scoped, preso as classes proprias que passamos por prop. -->
<style>
.acl-conversation-more-actions-menu .acl-more-actions-item {
  width: 100%;
  height: 3.5rem;
  min-height: 3.5rem;
  gap: 0.85rem;
  /* O padding lateral do item mora aqui, no botao — nao no rotulo. */
  padding: 0.375rem 0.9rem;
  justify-content: flex-start;
  color: #0a0a0a;
}

.acl-conversation-more-actions-menu .acl-more-actions-item,
.acl-conversation-more-actions-menu .acl-more-actions-item.text-n-slate-12,
.acl-conversation-more-actions-menu .acl-more-actions-item.text-n-ruby-11 {
  color: #0a0a0a;
}

.acl-conversation-more-actions-menu .acl-more-actions-item [class*='i-'] {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  color: #0a0a0a;
  background-color: #0a0a0a;
  font-size: 28px;
  opacity: 1;
}

/* Rotulo maior que o text-sm padrao do DropdownMenu — sem padding lateral,
   que e do botao. */
.acl-conversation-more-actions-menu .acl-more-actions-item__label {
  font-size: 1.15rem;
  font-weight: 500;
  color: #0a0a0a;
  padding-left: 0;
  padding-right: 0;
}
</style>
